"use client";

/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from "react";
import { useI18n } from "@/i18n/context";

const SECTIONS = [
  ["#context", "Context", "01"],
  ["#gaze", "Gaze", "02"],
  ["#experience", "Experience", "03"],
  ["#identity", "Identity", "04"],
  ["#when", "When", "05"],
  ["#ethics", "Ethics", "06"],
  ["#pricing", "Pricing", "07"],
  ["#contact", "Contact", "08"],
] as const;

export function SiteNav() {
  const { t, locale, toggleLocale } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Nav scroll state — scrim only appears below the hero.
  useEffect(() => {
    const hero = document.querySelector<HTMLElement>(".hero");
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      const heroH = hero ? hero.offsetHeight : 600;
      setPastHero(window.scrollY > heroH - 80);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll + escape-to-close while the menu is open.
  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("menu-open");
    };
  }, [menuOpen]);

  return (
    <>
      <nav className={`top${scrolled ? " scrolled" : ""}${pastHero ? " past-hero" : ""}`}>
        <a href="#home" className="wordmark" aria-label="WooZoo home">
          <img className="wm-text" src="/assets/woozoo-wordmark.png" alt="WooZoo" />
        </a>
        <div className="links">
          {SECTIONS.map(([href, label]) => (
            <a key={href} href={href}>
              {t(label)}
            </a>
          ))}
        </div>
        <div className="right-cluster">
          <button
            type="button"
            className="lang lang-toggle"
            aria-label="Toggle language English / French"
            aria-pressed={locale === "fr"}
            onClick={toggleLocale}
          >
            <span className={`${locale === "en" ? "active" : ""}`} data-lang="en">
              EN
            </span>
            <span className="sep">/</span>
            <span className={`${locale === "fr" ? "active" : ""}`} data-lang="fr">
              FR
            </span>
          </button>
          <a href="#b2b" className="cta-b2b">
            {t("WooZoo Business")}
          </a>
          <button
            className="burger"
            aria-label="Open menu"
            aria-controls="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="bars">
              <span />
              <span />
              <span />
            </span>
            <span className="burger-label">{t("Menu")}</span>
          </button>
        </div>
      </nav>

      <div
        className="menu-overlay"
        id="menu"
        role="dialog"
        aria-modal="true"
        aria-hidden={!menuOpen}
      >
        <div className="menu-inner">
          <nav className="menu-links">
            {SECTIONS.map(([href, label, n]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>
                <span className="n">{n}</span>
                {t(label)}
              </a>
            ))}
          </nav>
          <aside className="menu-side">
            <div className="grp">
              <h5>{t("Get the app")}</h5>
              <a href="#">{t("→ App Store")}</a>
              <a href="#">{t("→ Google Play")}</a>
            </div>
            <div className="grp">
              <h5>{t("For organizations")}</h5>
              <a href="#b2b">{t("→ WooZoo Business")}</a>
            </div>
            <div className="grp">
              <h5>{t("Follow")}</h5>
              <a href="https://instagram.com/woozoo">{t("→ Instagram")}</a>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
